import { getProductPackagingTemplate } from "@/api/template";
import FileDownloader from "@/components/FileDownloader/FileDownloader";
import FileUploader from "@/components/FileUploader/FileUploader";
import ImpactPill from "@/components/Impact/ImpactPill";
import Skeleton from "@/components/Skeleton/Skeleton";
import Table, { Column } from "@/components/Table/Table";
import TableHeader from "@/components/TableHeader/TableHeader";
import useAnalytics from "@/hooks/useAnalytics";
import useTranslation from "@/hooks/useTranslation";
import { formatMass } from "@/pages/dashboard/utils/formatters";
import {
  IPackagingBom,
  useDeletePackagingBom,
  useExportPackagingBom,
  usePackagingBoms,
} from "@/pages/packaging/usePackagings";
import styles from "@/pages/products/detail/bom/BomRow.module.scss";
import {
  DeleteOutlined,
  DownloadOutlined,
  ExclamationCircleFilled,
  ExportOutlined,
} from "@ant-design/icons";
import { Button, message, Popconfirm, Space, Tooltip } from "antd";
import { useParams } from "react-router-dom";

function PackagingBom() {
  let { packagingId: id } = useParams();
  const packagingId = Number(id);

  const analytics = useAnalytics();
  const exportBom = useExportPackagingBom();
  const deleteBom = useDeletePackagingBom();
  const translation = useTranslation();
  const columns: Column<IPackagingBom>[] = [
    {
      key: "name",
      label: "Material",
      minWidth: "376px",
      maxWidth: "376px",
      renderer: (record) => (
        <div className={styles["material-name"]}>
          <span>{record.name}</span>
          <span className={styles["supplier"]}>
            {[record.supplier_name, record.supplier_address].filter((x) => x).join(" - ")}
          </span>
        </div>
      ),
    },
    {
      key: "quantity",
      label: "Quantity",
      minWidth: "100px",
    },
    {
      key: "unit",
      label: "Unit",
      minWidth: "100px",
    },
    {
      key: "total_mass",
      label: "Total mass",
      minWidth: "100px",
      renderer: (record) => formatMass(record.mass),
    },
    {
      key: "impact",
      label: "Impact",
      minWidth: "300px",
      renderer: (record) => (
        <ImpactPill
          type="borderless"
          loading={false}
          impact={record.impact}
          showDate={false}
          incomplete={record.status === "incomplete"}
          incompleteTooltip={
            record.status === "incomplete"
              ? "Material needs an impact reference and/or unit conversion to calculate impact."
              : undefined
          }
        />
      ),
    },
  ];

  const { data, isLoading, isFetching } = usePackagingBoms({ packagingId });
  const bomAvailable = data?.bom?.length && data.bom.length > 0;
  return (
    <div>
      <TableHeader
        noRoundedTop
        left={
          isLoading ? (
            <>
              <Skeleton style={{ height: "32px", width: "230px" }} />
              <Skeleton style={{ height: "14px", width: "80px" }} />
            </>
          ) : (
            <Space.Compact>
              <FileUploader
                uploadType={"packaging"}
                uploadDataTableProps={{
                  columns: [
                    { key: "packaging_id", label: "Packaging ID" },
                    { key: "level", label: "Level" },
                    { key: "name", label: "Name" },
                    { key: "quantity", label: "Quantity" },
                    { key: "unit", label: "Unit" },
                    { key: "supplier_name", label: "Supplier name" },
                    { key: "supplier_address", label: "Supplier address" },
                  ],
                  rowKey: "id",
                }}
              />
              <FileDownloader
                fileOutputName={`template-product-packaging.xlsx`}
                fileUrlFn={getProductPackagingTemplate}
                buttonText="Template"
                icon={<DownloadOutlined />}
                onDownload={() => analytics.track("Packaging Template Downloaded")}
              />
              <FileDownloader
                tooltipText="Export"
                fileOutputName={`export-packaging.xlsx`}
                fileUrlFn={() => exportBom.mutateAsync({ packagingId }).then((data) => data.url)}
                buttonText={<ExportOutlined />}
                addTimestampAndCompany={true}
              />
              <Popconfirm
                title={"Delete bill of materials"}
                disabled={!bomAvailable}
                icon={<ExclamationCircleFilled style={{ color: "#F5222D" }} />}
                styles={{ body: { maxWidth: "400px" } }}
                description={
                  <>
                    <p>
                      Deleting the bill of materials only involves deleting structure of this
                      packaging, but does not delete the materials.
                    </p>

                    <p>This action can not be undone.</p>
                  </>
                }
                okText={"Delete"}
                okType={"primary"}
                okButtonProps={{
                  danger: true,
                  loading: deleteBom.isLoading,
                  disabled: deleteBom.isLoading,
                }}
                onConfirm={() =>
                  deleteBom
                    .mutateAsync({ packagingId })
                    .then((result) => {
                      if (result.status === 200) {
                        message.success("BOM deleted.");
                      }
                    })
                    .catch(() => message.error(translation.errors.general))
                }
              >
                <Tooltip title={"Delete BOM"}>
                  <Button
                    style={{ color: "red" }}
                    disabled={!bomAvailable}
                  >
                    <DeleteOutlined />
                  </Button>
                </Tooltip>
              </Popconfirm>
            </Space.Compact>
          )
        }
        right={
          isLoading ? (
            <Skeleton
              style={{
                width: "65px",
                height: "20px",
              }}
            />
          ) : (
            <span className={styles.total}>{data?.total_count} total</span>
          )
        }
      />
      <Table<IPackagingBom>
        loading={isLoading || isFetching}
        rowKey="id"
        data={data?.bom}
        columns={columns}
        rowLink={(record) => "/matching/packaging/materials/" + record.id}
        emptyState={{
          noResultsMessage: "No bill of materials available.",
        }}
      />
    </div>
  );
}

export default PackagingBom;
